import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useParentCredits } from "@/hooks/useParentCredits";
import { toast } from "@/hooks/use-toast";
import { CreditCard, Coins, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate, useSearchParams } from "react-router-dom";

type PaymentProvider = 'stripe' | 'mollie' | null;

export default function BuyCredits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ptaId, setPtaId] = useState<string | null>(null);
  const { balance, loading: balanceLoading, refetch } = useParentCredits(ptaId || undefined);
  const [purchasing, setPurchasing] = useState(false);
  const [ptaData, setPtaData] = useState<any>(null);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider>(null);
  const [providerLoading, setProviderLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchPTAId();
  }, [user, navigate]);

  useEffect(() => {
    if (ptaId) {
      fetchPTAData();
      detectPaymentProvider();
    }
  }, [ptaId]);

  useEffect(() => {
    if (ptaData && ptaData.show_marketplace_section === false) {
      toast({
        title: "Marketplace Unavailable",
        description: "The marketplace is not enabled for this PTA.",
        variant: "destructive"
      });
      navigate(`/fundraising/${ptaId}`);
    }
  }, [ptaData, navigate, ptaId]);

  const fetchPTAData = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .rpc('get_public_pta_info', { p_pta_id: ptaId })
      .single();
    if (data) setPtaData(data);
  };

  const fetchPTAId = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('parents')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();
    if (data) setPtaId(data.pta_id);
  };

  const detectPaymentProvider = async () => {
    if (!ptaId) return;
    setProviderLoading(true);
    try {
      const [stripeRes, mollieRes] = await Promise.all([
        supabase
          .from('pta_stripe_accounts')
          .select('id, onboarding_completed')
          .eq('pta_id', ptaId)
          .maybeSingle(),
        supabase
          .rpc('get_pta_mollie_account_safe', { p_pta_id: ptaId })
          .maybeSingle(),
      ]);

      if (stripeRes.data?.onboarding_completed) {
        setPaymentProvider('stripe');
      } else if (mollieRes.data?.can_receive_payments) {
        setPaymentProvider('mollie');
      } else {
        setPaymentProvider(null);
      }
    } catch (err) {
      console.error('Error detecting payment provider:', err);
      setPaymentProvider(null);
    } finally {
      setProviderLoading(false);
    }
  };

  const creditPackages = [
    { credits: 5, price: 5, discount: 0, popular: false },
    { credits: 10, price: 9, discount: 10, popular: true },
    { credits: 20, price: 16, discount: 20, popular: false },
  ];

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      refetch();
      toast({
        title: "Payment Successful!",
        description: "Your credits have been added to your account.",
      });
      window.history.replaceState({}, '', '/buy-credits');
    } else if (payment === 'cancelled') {
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. No credits were purchased.",
        variant: "destructive"
      });
      window.history.replaceState({}, '', '/buy-credits');
    }
  }, [searchParams]);

  const handlePurchase = async (credits: number, price: number) => {
    if (!user || !ptaId) {
      toast({ title: "Error", description: "Please make sure you're registered as a parent", variant: "destructive" });
      return;
    }

    if (!paymentProvider) {
      toast({ title: "Payment Unavailable", description: "No payment provider is configured for this PTA. Please contact your PTA admin.", variant: "destructive" });
      return;
    }

    setPurchasing(true);

    try {
      if (paymentProvider === 'stripe') {
        const { data, error } = await supabase.functions.invoke('create-credit-purchase-checkout', {
          body: { ptaId, credits, price }
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (data?.url) {
          window.location.href = data.url;
        } else {
          throw new Error('No checkout URL returned');
        }
      } else if (paymentProvider === 'mollie') {
        const { data, error } = await supabase.functions.invoke('mollie-create-payment', {
          body: {
            ptaId,
            paymentType: 'credit_purchase',
            amount: price,
            currency: 'GBP',
            description: `Marketplace Credits (${credits})`,
            creditsAmount: credits,
            parentId: user.id,
          }
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (data?.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          throw new Error('No checkout URL returned');
        }
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast({
        title: "Payment Error",
        description: error.message || "Failed to start checkout. Please try again.",
        variant: "destructive"
      });
      setPurchasing(false);
    }
  };

  if (!user) return null;

  const isLoading = balanceLoading || providerLoading;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Buy Credits</h1>
            <p className="text-muted-foreground">
              Purchase credits to list items in the marketplace
            </p>
          </div>

          {isLoading ? (
            <Card>
              <CardContent className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coins className="h-5 w-5" />
                    Your Credit Balance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-4xl font-bold text-primary">
                        {balance?.credits_available || 0}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Available Credits
                      </div>
                    </div>
                    {(balance?.credits_pending || 0) > 0 && (
                      <div className="text-right">
                        <div className="text-2xl font-semibold text-orange-600">
                          {balance.credits_pending}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Pending Approval
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {!paymentProvider ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No payment provider is configured for this PTA. Please contact your PTA admin to set up payments before purchasing credits.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Credits are used to list items for sale in the marketplace. Each listing costs 1 credit (or as set by your PTA). Credits purchased support your PTA!
                    </AlertDescription>
                  </Alert>

                  <div className="grid md:grid-cols-3 gap-6">
                    {creditPackages.map((pkg) => (
                      <Card
                        key={pkg.credits}
                        className={pkg.popular ? "border-primary border-2 shadow-lg" : ""}
                      >
                        {pkg.popular && (
                          <div className="bg-primary text-primary-foreground text-center py-1 text-sm font-semibold">
                            Most Popular
                          </div>
                        )}
                        <CardHeader>
                          <CardTitle className="text-center">
                            <div className="text-4xl font-bold text-primary mb-2">
                              {pkg.credits}
                            </div>
                            <div className="text-lg font-normal text-muted-foreground">
                              Credits
                            </div>
                          </CardTitle>
                          <CardDescription className="text-center">
                            <div className="text-3xl font-bold text-foreground">
                              £{pkg.price}
                            </div>
                            {pkg.discount > 0 && (
                              <Badge variant="secondary" className="mt-2">
                                Save {pkg.discount}%
                              </Badge>
                            )}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Button
                            className="w-full"
                            onClick={() => handlePurchase(pkg.credits, pkg.price)}
                            disabled={purchasing}
                            variant={pkg.popular ? "default" : "outline"}
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            {purchasing ? "Processing..." : "Buy Now"}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>How It Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">1.</span>
                    <p>Purchase credits using your debit/credit card via secure online payment</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">2.</span>
                    <p>Credits are added to your account instantly</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">3.</span>
                    <p>Use credits to list items for sale in the marketplace</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">4.</span>
                    <p>If your listing is rejected, credits are refunded automatically</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-primary">5.</span>
                    <p>All credit purchases go directly to your PTA to support school activities</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
